import { action, observable, runInAction, makeObservable } from 'mobx';
import { eventBus, subscribe } from 'mobx-event-bus2';
import { AxiosError } from 'axios';

import { reverse } from 'named-urls';

import DataMixin from 'utils/stores/DataMixin';

import Logger from 'utils/logger';

import API from 'utils/api';
import { EventType } from 'utils/events/constants';
import { TaskListResponse } from 'utils/api/modules/tasks/tasks.types';
import { isNotFound } from 'utils/api/errors';
import { postMessage } from 'utils/events/broadcast';

import { findLinkByEntity } from 'utils/api/modules/tasks/tasks.utils';
import { InspectionEntity } from 'utils/api/modules/workflows/workflows.types';
import routes from 'core/routes';

import TaskActivities from './TaskActivities';
import RootStore from '../Root';

export default class TaskDetailsStore extends DataMixin {
  @observable error?: AxiosError;

  @observable details?: TaskListResponse;

  @observable activities?: TaskActivities;

  constructor(rootStore: RootStore, api: typeof API) {
    super(rootStore, api);

    makeObservable(this);

    eventBus.register(this);
  }

  @action.bound
  async loadDetails(taskId: string): Promise<void> {
    this.isLoaded = false;
    try {
      const { data } = await this.api.tasks.loadTaskDetails(taskId)();
      runInAction(() => {
        this.activities = new TaskActivities(this.store, this.api, taskId);
        this.error = undefined;
        this.details = data;
      });
    } catch (e) {
      runInAction(() => {
        // @ts-ignore
        this.error = e;
      });
      // @ts-ignore
      if (!isNotFound(e)) {
        Logger.error(`Invalid load task details API response. Task id: ${taskId}`, e);
      }
    } finally {
      runInAction(() => {
        this.isLoaded = true;
      });
    }
  }

  @action.bound
  async createTask(name: string, assigned: string, dueDate: string | null): Promise<boolean> {
    try {
      const { data } = await this.api.tasks.createTask({
        name,
        assigned,
        dueDate,
      })();
      this.store.notification.enqueueSuccessSnackbar('New action created');
      postMessage(EventType.CreatedTask, data);

      return true;
    } catch (e) {
      this.store.notification.enqueueErrorSnackbar('The action could not be created. Something went wrong.');
      return false;
    }
  }

  @action
  async deleteTask(taskId: string): Promise<boolean> {
    try {
      await this.api.tasks.deleteTask(taskId)();
      postMessage(EventType.DeletedTask, { taskId });
      this.store.notification.enqueueSuccessSnackbar('Task deleted successfully');
      return true;
    } catch (ex) {
      Logger.error('Invalid delete task API response.', ex);
      this.store.notification.enqueueErrorSnackbar('Task could not be deleted');
      return false;
    }
  }

  @action
  async updateTask(taskData: Partial<TaskListResponse>): Promise<boolean> {
    try {
      const { data } = await this.api.tasks.updateTask(this.details?.uuid as string, taskData)();
      this.store.notification.enqueueSuccessSnackbar('Task updated successfully');
      postMessage(EventType.UpdatedTask, data);
      runInAction(() => {
        this.details = data;
      });
      return true;
    } catch (ex) {
      Logger.error('Invalid update task API response.', ex);
      this.store.notification.enqueueErrorSnackbar('Task could not be updated');
      throw ex;
    }
  }

  @action
  async createAuditForm(): Promise<void> {
    if (!this.details?.template) {
      Logger.error(`Template not found for task ${this.details?.uuid}`, new Error('Template not found'));
      return;
    }

    // start the audit
    try {
      const { data } = await API.tasks.createTaskFromTemplate(this.details.uuid)();
      if (data) {
        const inspectionLink = findLinkByEntity(data.links, InspectionEntity);
        if (!inspectionLink) {
          Logger.error(`Inspection link not found for task ${this.details?.uuid}`);
          this.store.notification.enqueueErrorSnackbar('Inspection link not found');
          return;
        }
        const formId = inspectionLink.instance.uuid as string;
        this.store.routing.push(reverse(routes.dashboard.audits.forms.details, { formId }));
      }
    } catch (error) {
      Logger.error(`Failed to start the audit for task ${this.details?.uuid}`, error);
      this.store.notification.enqueueErrorSnackbar('Failed to start the audit');
    }
  }

  @action
  async continueAuditForm(): Promise<void> {
    const inspectionLink = findLinkByEntity(this.details?.links, InspectionEntity);
    if (!inspectionLink) {
      Logger.error(`Inspection link not found for task ${this.details?.uuid}`);
      this.store.notification.enqueueErrorSnackbar('Inspection link not found');
      return;
    }

    const formId = inspectionLink.instance.uuid as string;
    this.store.routing.push(reverse(routes.dashboard.audits.forms.details, { formId }));
  }

  @subscribe(EventType.LoggedOut)
  @action
  reset(): void {
    this.isLoaded = false;
    this.error = undefined;
    this.details = undefined;
  }
}
